import { Link } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Instagram, Facebook, Youtube, Scissors, ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";
const heroHome = "/hero-home.webp";

const LazyMotionDiv = lazy(() =>
  import("framer-motion").then((m) => ({ default: m.motion.div }))
);

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.75a8.18 8.18 0 004.77 1.52V6.84a4.84 4.84 0 01-1-.15z" />
  </svg>
);

const socialLinks = [
  { icon: <Instagram size={18} />, label: "Instagram", href: "https://www.instagram.com/cutsbycate?igsh=MWUyenRmb2htMjA3Nw%3D%3D&utm_source=qr" },
  { icon: <TikTokIcon />, label: "TikTok", href: "#" },
  { icon: <Facebook size={18} />, label: "Facebook", href: "#" },
  { icon: <Youtube size={18} />, label: "YouTube", href: "#" },
];

const Index = () => {
  return (
    <Layout>
      {/* Hero Section — split layout for readability */}
      <section className="relative min-h-[92vh] overflow-hidden bg-[#FBF7F4]">
        <div className="flex flex-col lg:flex-row min-h-[92vh]">
          {/* Text Side */}
          <div className="flex items-center justify-center lg:w-[45%] px-8 py-16 lg:py-0 animate-hero-fade-in order-2 lg:order-1">
            <div className="text-center lg:text-left max-w-md">
              <p className="font-sans text-[11px] font-bold tracking-[0.35em] uppercase mb-6 text-foreground/50">
                Tulsa, Oklahoma
              </p>
              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-medium mb-4 italic text-foreground">
                Cuts By Cate
              </h1>
              <div className="decorative-line lg:mx-0 mb-6" />
              <p className="font-sans text-base md:text-lg font-light max-w-sm mb-12 text-foreground/50 leading-relaxed">
                Elevating your look with artistry & intention
              </p>
              <Link
                to="/book"
                className="group inline-flex items-center gap-3 border border-foreground/30 text-foreground px-10 py-4 text-[11px] font-sans font-bold tracking-[0.2em] uppercase hover:bg-foreground hover:text-background transition-all duration-500"
              >
                Book an Appointment
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
          </div>

          {/* Image Side */}
          <div className="lg:w-[55%] min-h-[60vh] lg:min-h-full order-1 lg:order-2">
            <img
              src={heroHome}
              alt="Catelin Parr - Hair Stylist"
              width={768}
              height={1024}
              sizes="(min-width: 1024px) 55vw, 100vw"
              fetchPriority="high"
              decoding="async"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* Social Bar */}
      <section className="bg-secondary/90 marble-texture py-5">
        <div className="container mx-auto px-6 flex justify-center gap-10">
          {socialLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={link.label}
              className="text-secondary-foreground/50 hover:text-secondary-foreground transition-colors duration-300"
            >
              {link.icon}
            </a>
          ))}
        </div>
      </section>

      {/* About Section — lazy-loaded framer-motion for below-the-fold */}
      <section className="section-padding">
        <div className="container mx-auto px-6 max-w-2xl text-center">
          <Suspense fallback={<div className="animate-fade-in"><AboutContent /></div>}>
            <LazyMotionDiv
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <AboutContent />
            </LazyMotionDiv>
          </Suspense>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary marble-texture text-primary-foreground py-24 md:py-32">
        <div className="container mx-auto px-6 text-center">
          <Suspense fallback={<div className="animate-fade-in"><CtaContent /></div>}>
            <LazyMotionDiv
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <CtaContent />
            </LazyMotionDiv>
          </Suspense>
        </div>
      </section>
    </Layout>
  );
};

const AboutContent = () => (
  <>
    <Scissors className="mx-auto mb-8 text-primary/60" size={24} strokeWidth={1.5} />
    <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl mb-3 italic">
      Meet Catelin
    </h2>
    <div className="decorative-line mb-8" />
    <p className="font-sans text-muted-foreground leading-[1.8] text-base mb-5">
      With a passion for transforming looks and boosting confidence, Catelin Parr brings
      artistry and precision to every appointment. Based in Tulsa, Oklahoma, she
      specializes in cuts, color, and styling that reflect your unique personality.
    </p>
    <p className="font-sans text-muted-foreground leading-[1.8] text-base mb-12">
      Whether you're looking for a bold new style or a subtle refresh, Catelin is
      dedicated to making you look and feel your absolute best.
    </p>
    <Link
      to="/services"
      className="group inline-flex items-center gap-2 text-[11px] font-sans font-bold tracking-[0.2em] uppercase text-foreground hover:text-primary transition-colors duration-300"
    >
      View Services
      <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
    </Link>
  </>
);

const CtaContent = () => (
  <>
    <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl mb-3 italic">
      Ready for a New Look?
    </h2>
    <div className="w-12 h-px bg-primary-foreground/30 mx-auto mb-8" />
    <p className="font-sans text-sm opacity-60 mb-12 max-w-sm mx-auto leading-relaxed">
      Book your appointment today and let Catelin craft the perfect style for you.
    </p>
    <Link
      to="/book"
      className="group inline-flex items-center gap-3 border border-primary-foreground/30 text-primary-foreground px-10 py-4 text-[11px] font-sans font-bold tracking-[0.2em] uppercase hover:bg-primary-foreground hover:text-primary transition-all duration-500"
    >
      Book Now
      <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
    </Link>
  </>
);

export default Index;
